/**
 * `pending-shared-ui-primitives` v0.2 — `<Card />`.
 *
 * Token-driven container primitive that mirrors the Button + Input +
 * Modal aesthetic. Consolidates the 12+ ad-hoc card classes scattered
 * across the apps (`.schedule-card`, `.project-card`, `.cron-chip-
 * picker`, `.modal-card`, the catalogue tiles, …) onto a single shape
 * the 5 apps can converge on.
 *
 * Apps that consume this primitive MUST already import
 * `@rynora/ui/ui.css` once at the root (handled by the same import
 * the v0 Button + Input ship needed).
 *
 * Variants:
 *   - `default`  : 1px border + subtle inner background.
 *   - `elevated` : adds the theme's `--shadow-md` so the card lifts
 *                  off the page (used for action surfaces and
 *                  decision tiles).
 *   - `outlined` : transparent background + accent-coloured border
 *                  (used for emphasis on a busy page).
 *   - `flat`     : background only, no border. Useful for nested
 *                  cards that should not double up borders.
 *
 * Sub-components:
 *   - `<CardHeader>` — flex row with title + optional action slot.
 *   - `<CardTitle>` — typographic header (h3 by default; the `as`
 *     prop polymorphism is intentionally NOT in v0.2 — every shipped
 *     card uses h3 today, the polymorphism is a v0.3 ask).
 *   - `<CardBody>` — content slot. The default vertical rhythm uses
 *     the same space-3 token Modal uses for its body.
 *   - `<CardFooter>` — actions strip at the bottom.
 *
 * # v0.4 — polymorphic `as` prop on `<Card>`
 *
 * Closes the v0.2 doc-block deferral. Pass `as="article"` to render
 * a card semantically as an `<article>`; pass `as="section"` for
 * landmark sections; pass `as={SomeComponent}` to render the card as
 * any React component (e.g. a `react-router` `<Link>` to make the
 * card itself a link). Defaults to `'div'` so all existing callers
 * are byte-identical.
 *
 *   <Card as="article">…</Card>
 *   <Card as={Link} to="/dashboard">…</Card>
 *
 * The polymorphism is intentionally scoped to `<Card>` only — every
 * shipped consumer of `<CardHeader>` / `<CardTitle>` / `<CardBody>`
 * / `<CardFooter>` uses them as plain `<div>` / `<h3>` / `<div>` /
 * `<div>`, and adding `as` to all five would balloon the type
 * surface for zero callers. We'll widen if a consumer asks.
 *
 * v0.2 explicitly does NOT do:
 *   - Interactive (`onClick`) card pattern. Cards that are entirely
 *     clickable should use `<Card as={Link}>` (v0.4) or wrap a
 *     `<Button>` — putting an `onClick` on a `<div>`-rendered card
 *     breaks keyboard accessibility unless we add the full
 *     role="button" / tabIndex / Enter+Space dance.
 *   - `loading` skeleton state. Cards render their actual content;
 *     skeletons are a higher-level pattern.
 */

import { forwardRef, createElement } from 'react';
import type { ComponentPropsWithoutRef, ElementType, HTMLAttributes, ReactNode, Ref } from 'react';

export type CardVariant = 'default' | 'elevated' | 'outlined' | 'flat';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

/**
 * `CardProps` is generic over the rendered element so the prop list
 * comes from whatever tag/component the caller picked via `as`.
 *
 * `<Card as="a" href="…">` → href is required on `<a>`, so TS shows
 * it as a required-when-relevant prop. `<Card as="article">` → the
 * `<article>` HTML attributes apply. `<Card>` (no `as`) → `<div>`
 * attributes apply, matching the v0.2 contract.
 */
export type CardProps<C extends ElementType = 'div'> = {
  variant?: CardVariant;
  padding?: CardPadding;
  children?: ReactNode;
  /** Render the card as a different element/component. Defaults to `'div'`. */
  as?: C;
} & Omit<ComponentPropsWithoutRef<C>, 'as' | 'variant' | 'padding' | 'children'>;

const VARIANT_CLASSNAME: Record<CardVariant, string> = {
  default: 'rui-card rui-card--default',
  elevated: 'rui-card rui-card--elevated',
  outlined: 'rui-card rui-card--outlined',
  flat: 'rui-card rui-card--flat',
};

const PADDING_CLASSNAME: Record<CardPadding, string> = {
  none: 'rui-card--pad-none',
  sm: 'rui-card--pad-sm',
  md: 'rui-card--pad-md',
  lg: 'rui-card--pad-lg',
};

// `forwardRef` doesn't play nicely with generics, so we hand-roll a
// component with a typed ref forward + cast the function shape at
// export time. This matches the polymorphic-component pattern in
// Radix UI / Chakra / Mantine; it keeps both call-site inference and
// `ref` forwarding working.
function CardInner<C extends ElementType = 'div'>(props: CardProps<C>, ref: Ref<Element>) {
  const { as, variant = 'default', padding = 'md', className, children, ...rest } = props;
  const Component = (as ?? 'div') as ElementType;
  const cls = [VARIANT_CLASSNAME[variant], PADDING_CLASSNAME[padding], className].filter(Boolean).join(' ');
  return createElement(Component, { ref, className: cls, ...rest }, children);
}

export const Card = forwardRef(CardInner) as <C extends ElementType = 'div'>(
  props: CardProps<C> & { ref?: Ref<Element> },
) => ReturnType<typeof CardInner>;

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Optional right-aligned slot — typically icon buttons / overflow menu. */
  actions?: ReactNode;
  children?: ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(props, ref) {
  const { actions, className, children, ...rest } = props;
  const cls = ['rui-card__header', className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} {...rest}>
      <div className="rui-card__header-main">{children}</div>
      {actions ? <div className="rui-card__header-actions">{actions}</div> : null}
    </div>
  );
});

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  children?: ReactNode;
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(function CardTitle(props, ref) {
  const { className, children, ...rest } = props;
  const cls = ['rui-card__title', className].filter(Boolean).join(' ');
  return (
    <h3 ref={ref} className={cls} {...rest}>
      {children}
    </h3>
  );
});

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(function CardBody(props, ref) {
  const { className, children, ...rest } = props;
  const cls = ['rui-card__body', className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} {...rest}>
      {children}
    </div>
  );
});

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(function CardFooter(props, ref) {
  const { className, children, ...rest } = props;
  const cls = ['rui-card__footer', className].filter(Boolean).join(' ');
  return (
    <div ref={ref} className={cls} {...rest}>
      {children}
    </div>
  );
});
